// #region: Class definition *********************************************************************/
/**
 * @class ProfileMenu
 * @description Custom web component that provides a profile menu.
 * 
 * @extends DropDown
 * Extends the DropDown web component.
 *
 * @example
 * <profile-menu>
 *     <button class="profile-label">
 *         {MenuLabel}</span>
 *     </button>
 *     <div class="profile-content">
 *         {MenuContent}
 *     </div>
 * </profile-menu>
 */
 
class ProfileMenu extends DropDown {

    // #region: Private Fields *******************************************************************/
    /**
     * Interval ID for updating the part of day label.
     * @type {number|null}
     * 
     * @private
     */
    #partOfDayInterval;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of ProfileMenu.
     */
    constructor() {
        super('.profile-button');

        // We want default right alignment.
        this.alignRight = true;

        // What part of the day is it?
        /* eslint-disable no-magic-numbers -- This is a function running every hour. */
        this.#partOfDayInterval = setInterval(() => {
            this.#getPartOfDay();
        }, 60000);

        this.#getPartOfDay();
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     * @memberof ProfileMenu
     */
    static tagName = 'profile-menu';

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance.
     */
    toString() {
        return '[ProfileMenu]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     */
    static toString() {
        return '[ProfileMenu]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class.
     * 
     * @static
     */
    static version() {
        return '1.0.0';
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Cleanup. Called when the component is removed from the DOM.
     * Extend to clean up the part of day interval.
     */
    disconnectedCallback() {
        super.disconnectedCallback();

        // Remove interval.
        clearInterval(this.#partOfDayInterval);
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Validate the content.
     * @returns {boolean} True if valid, false if not. Defaults to true.
     * 
     * @memberof ProfileMenu
     */
    validate() {
        return true;
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Set the day part label, i.e. 'Good morning', etc.
     * 
     * @private
     */
    /* eslint-disable no-magic-numbers -- These are time numbers. */
    #getPartOfDay() {
        const dayPartLabel = this.querySelector('#daypart-label');
        const hours = new Date().getHours();

        let partOfDay = 'night';
        if (hours >= 5 && hours < 12) {
            partOfDay = 'morning';
        }
        else if (hours >= 12 && hours < 17) {
            partOfDay = 'afternoon';
        }
        else if (hours >= 17 && hours < 21) {
            partOfDay = 'evening';
        }

        dayPartLabel.textContent = resources.partOfDay[partOfDay] + ', ';
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

/**
 * Register web component.
 * Defines the custom element 'profile-menu' and maps it to the ProfileMenu class.
 */
customElements.define(ProfileMenu.tagName, ProfileMenu);
